/**
 * The device pixel ratio to rasterise map markers at, clamped to [1, 3]. MapLibre renders the
 * map canvas at `devicePixelRatio`; a marker bitmap registered at 1× is upscaled on hi-DPI
 * displays and looks blurry. Rasterising at this ratio (and registering the image with a
 * matching `{ pixelRatio }` on `map.addImage`) keeps markers crisp while leaving their on-map
 * display size unchanged. Clamped to 3 to bound texture memory on 4K/hi-DPI screens.
 */
export function getMarkerPixelRatio(): number {
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    return Math.min(Math.max(dpr, 1), 3);
}

/**
 * Overwrites the root `<svg>` element's `width`/`height` attributes (leaving `viewBox` and
 * everything else untouched) so the browser rasterises the vector at the given device-pixel size.
 * A browser rasterises an SVG `<img>` at its *intrinsic* (width/height attribute) size, so drawing
 * a 52×60 SVG larger via `drawImage` only re-samples that small bitmap and stays blurry. Sizing
 * the root to the device-pixel dimensions is what actually makes it crisp. All marker SVGs declare
 * width/height on the root; if a future one only has a viewBox the replace is a no-op and it falls
 * back to the previous (upscaled) behaviour rather than breaking.
 */
function withRootSize(svg: string, width: number, height: number): string {
    return svg
        .replace(/(<svg\b[^>]*?)\swidth="[^"]*"/, `$1 width="${String(width)}"`)
        .replace(/(<svg\b[^>]*?)\sheight="[^"]*"/, `$1 height="${String(height)}"`);
}

/**
 * Rasterises an SVG string into `ImageData` for use with `map.addImage`. Loads the SVG via a
 * blob URL into an `<img>`, draws it to a 2D canvas and reads back the pixels. Rejects if the
 * canvas context is unavailable or the SVG fails to load.
 *
 * `width`/`height` are the marker's CSS pixel size; `pixelRatio` scales the backing bitmap (the
 * SVG is rasterised at `width*pixelRatio × height*pixelRatio` and the canvas is sized to match).
 * Pass the same `pixelRatio` to `map.addImage(id, imageData, { pixelRatio })` so the on-map
 * display size stays `width × height` while the bitmap carries the extra resolution.
 *
 * Note: SVG `<filter>` effects (feGaussianBlur / drop-shadow) do NOT rasterise reliably
 * through this path — bake shadows with plain shapes/gradients instead.
 */
export async function loadSvgAsImageData(
    svg: string,
    width: number,
    height: number,
    pixelRatio = 1,
): Promise<ImageData> {
    const scaledWidth = Math.round(width * pixelRatio);
    const scaledHeight = Math.round(height * pixelRatio);
    const scaledSvg = withRootSize(svg, scaledWidth, scaledHeight);
    return new Promise((resolve, reject) => {
        const blob = new Blob([scaledSvg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        // Intrinsic size = device-pixel size, so the vector rasterises crisply and we draw it 1:1.
        const img = new Image(scaledWidth, scaledHeight);
        img.onload = () => {
            URL.revokeObjectURL(url);
            const canvas = document.createElement('canvas');
            canvas.width = scaledWidth;
            canvas.height = scaledHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('No 2D canvas context'));
                return;
            }
            ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);
            resolve(ctx.getImageData(0, 0, scaledWidth, scaledHeight));
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('SVG image load failed'));
        };
        img.src = url;
    });
}
